export type RuntimePostgresClientFailureReason =
  | "connection_closed"
  | "connection_timeout"
  | "operation_timeout"
  | "socket_closed";

export type RuntimePostgresClientOperation = "unsafe" | "begin";

type RuntimePostgresLogger = Pick<Console, "warn" | "error">;

type RuntimePostgresSqlClient = {
  unsafe(query: string, params?: unknown[]): Promise<unknown> & {
    values?: () => Promise<unknown>;
  };
  begin<T>(callback: (client: unknown) => Promise<T> | T): Promise<T>;
  close(): Promise<void> | void;
};

type RuntimePostgresUnsafeQueryResult = ReturnType<RuntimePostgresSqlClient["unsafe"]>;

export type RuntimePostgresClientDiagnostics = {
  label: string;
  databaseName?: string;
  generation: number;
  recoveries: number;
  lastFailure?: {
    at: string;
    operation: RuntimePostgresClientOperation;
    reason: RuntimePostgresClientFailureReason;
    elapsedMs: number;
    causeCode?: string;
    causeName?: string;
  };
};

export class RuntimePostgresClientError extends Error {
  readonly code: string;
  readonly reason: RuntimePostgresClientFailureReason;
  readonly operation: RuntimePostgresClientOperation;
  readonly generation: number;
  readonly elapsedMs: number;

  constructor(params: {
    reason: RuntimePostgresClientFailureReason;
    operation: RuntimePostgresClientOperation;
    generation: number;
    elapsedMs: number;
  }) {
    super(`Postgres client ${params.reason} during ${params.operation}; connection recycled`);
    this.name = "RuntimePostgresClientError";
    this.code = codeForFailureReason(params.reason);
    this.reason = params.reason;
    this.operation = params.operation;
    this.generation = params.generation;
    this.elapsedMs = params.elapsedMs;
  }
}

export type RuntimePostgresClient<TClient extends RuntimePostgresSqlClient> = TClient & {
  getDiagnostics(): RuntimePostgresClientDiagnostics;
};

function codeForFailureReason(reason: RuntimePostgresClientFailureReason): string {
  switch (reason) {
    case "connection_closed":
      return "GENCOW_DB_CONNECTION_CLOSED";
    case "connection_timeout":
      return "GENCOW_DB_CONNECTION_TIMEOUT";
    case "operation_timeout":
      return "GENCOW_DB_OPERATION_TIMEOUT";
    case "socket_closed":
      return "GENCOW_DB_SOCKET_CLOSED";
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function errorCode(error: unknown): string | undefined {
  const code = (error as { code?: unknown })?.code;
  return typeof code === "string" ? code : undefined;
}

function errorName(error: unknown): string | undefined {
  const name = (error as { name?: unknown })?.name;
  return typeof name === "string" ? name : undefined;
}

export function classifyPostgresClientFailure(error: unknown): RuntimePostgresClientFailureReason | null {
  if (error instanceof RuntimePostgresClientError) {
    return error.reason;
  }

  const code = errorCode(error);
  if (code === "ERR_POSTGRES_CONNECTION_CLOSED") {
    return "connection_closed";
  }
  if (code === "ECONNRESET" || code === "EPIPE") {
    return "socket_closed";
  }
  if (code === "ETIMEDOUT" || code === "ECONNREFUSED") {
    return "connection_timeout";
  }

  const message = errorMessage(error).toLowerCase();
  if (message.includes("socket connection was closed") || message.includes("socket closed")) {
    return "socket_closed";
  }
  if (
    message.includes("connection closed") ||
    message.includes("client is closed") ||
    message.includes("connection terminated") ||
    message.includes("closed unexpectedly")
  ) {
    return "connection_closed";
  }
  if (
    message.includes("connection timeout") ||
    message.includes("connect timeout") ||
    message.includes("connection timed out")
  ) {
    return "connection_timeout";
  }

  return null;
}

function safeDatabaseName(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return /^[a-zA-Z0-9_-]+$/.test(value) ? value : undefined;
}

function safeFailureCode(error: unknown): string | undefined {
  const code = errorCode(error);
  if (!code) return undefined;
  return /^[A-Z0-9_]+$/.test(code) ? code : "UNKNOWN";
}

function safeFailureName(error: unknown): string | undefined {
  const name = errorName(error);
  if (!name) return undefined;
  return /^[a-zA-Z0-9_]+$/.test(name) ? name : "Error";
}

async function closeWithBoundedWait(client: RuntimePostgresSqlClient, timeoutMs: number): Promise<void> {
  try {
    await Promise.race([
      Promise.resolve(client.close()),
      new Promise<void>((resolve) => {
        setTimeout(resolve, timeoutMs).unref?.();
      }),
    ]);
  } catch {
    /* Closing a broken SQL client is best-effort. */
  }
}

type RuntimePostgresUnsafeResult<T> = Promise<T> & {
  values: () => Promise<unknown>;
};

function rowsToValues(rows: unknown): unknown {
  if (!Array.isArray(rows)) return rows;
  return rows.map((row) => {
    if (Array.isArray(row)) return row;
    if (row && typeof row === "object") return Object.values(row as Record<string, unknown>);
    return [row];
  });
}

function attachUnsafeValues<T>(
  result: Promise<T>,
  values: () => Promise<unknown>,
): RuntimePostgresUnsafeResult<T> {
  Object.defineProperty(result, "values", {
    configurable: true,
    enumerable: false,
    value: values,
  });
  return result as RuntimePostgresUnsafeResult<T>;
}

export function createRuntimePostgresClient<TClient extends RuntimePostgresSqlClient>(params: {
  createClient: () => TClient;
  label: string;
  databaseName?: string;
  operationTimeoutMs: number;
  logger?: RuntimePostgresLogger;
}): RuntimePostgresClient<TClient> {
  const logger = params.logger ?? console;
  const databaseName = safeDatabaseName(params.databaseName);
  const operationTimeoutMs = Math.max(0, Math.floor(params.operationTimeoutMs));
  let activeClient = params.createClient();
  let generation = 1;
  let recoveries = 0;
  let lastFailure: RuntimePostgresClientDiagnostics["lastFailure"];

  const recycleClient = async (recycleParams: {
    client: TClient;
    expectedGeneration: number;
    operation: RuntimePostgresClientOperation;
    reason: RuntimePostgresClientFailureReason;
    elapsedMs: number;
    cause: unknown;
  }) => {
    if (activeClient !== recycleParams.client || generation !== recycleParams.expectedGeneration) {
      return;
    }

    const previousClient = activeClient;
    activeClient = params.createClient();
    generation += 1;
    recoveries += 1;
    lastFailure = {
      at: new Date().toISOString(),
      operation: recycleParams.operation,
      reason: recycleParams.reason,
      elapsedMs: recycleParams.elapsedMs,
      causeCode: safeFailureCode(recycleParams.cause),
      causeName: safeFailureName(recycleParams.cause),
    };

    const dbInfo = databaseName ? ` db=${databaseName}` : "";
    const causeCode = lastFailure.causeCode ? ` causeCode=${lastFailure.causeCode}` : "";
    const causeName = lastFailure.causeName ? ` causeName=${lastFailure.causeName}` : "";
    logger.warn(
      `[db] postgres client recycled label=${params.label}${dbInfo} reason=${recycleParams.reason} operation=${recycleParams.operation} elapsedMs=${recycleParams.elapsedMs} generation=${generation} recoveries=${recoveries}${causeName}${causeCode}`,
    );

    await closeWithBoundedWait(previousClient, 250);
  };

  const runOperation = async <T>(
    operation: RuntimePostgresClientOperation,
    run: (client: TClient) => Promise<T> | T,
  ): Promise<T> => {
    const client = activeClient;
    const operationGeneration = generation;
    const startedAt = Date.now();
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const work = Promise.resolve().then(() => run(client));
    work.catch(() => {
      /* The race below may time out first; keep late rejections handled. */
    });

    try {
      if (operationTimeoutMs <= 0) {
        return await work;
      }

      return await Promise.race([
        work,
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => {
            reject(
              new RuntimePostgresClientError({
                reason: "operation_timeout",
                operation,
                generation: operationGeneration,
                elapsedMs: Date.now() - startedAt,
              }),
            );
          }, operationTimeoutMs);
          timeout.unref?.();
        }),
      ]);
    } catch (error) {
      const reason = classifyPostgresClientFailure(error);
      if (!reason) {
        throw error;
      }

      const elapsedMs =
        error instanceof RuntimePostgresClientError ? error.elapsedMs : Date.now() - startedAt;
      await recycleClient({
        client,
        expectedGeneration: operationGeneration,
        operation,
        reason,
        elapsedMs,
        cause: error,
      });

      if (error instanceof RuntimePostgresClientError) {
        throw error;
      }
      throw new RuntimePostgresClientError({
        reason,
        operation,
        generation: operationGeneration,
        elapsedMs,
      });
    } finally {
      if (timeout !== undefined) {
        clearTimeout(timeout);
      }
    }
  };

  const target = {
    unsafe(query: string, queryParams?: unknown[]) {
      const queryClient = activeClient;
      let unsafeResult: RuntimePostgresUnsafeQueryResult | undefined;
      const getUnsafeResult = (client: TClient): RuntimePostgresUnsafeQueryResult => {
        if (client !== queryClient) {
          return client.unsafe(query, queryParams);
        }
        unsafeResult ??= queryClient.unsafe(query, queryParams);
        return unsafeResult;
      };

      return attachUnsafeValues(
        runOperation("unsafe", (client) => getUnsafeResult(client)),
        () =>
          runOperation("unsafe", async (client) => {
            const result = getUnsafeResult(client);
            if (typeof result.values === "function") {
              return result.values();
            }
            return rowsToValues(await result);
          }),
      );
    },
    begin<T>(callback: (client: unknown) => Promise<T> | T) {
      return runOperation("begin", (client) => client.begin(callback));
    },
    close() {
      return closeWithBoundedWait(activeClient, 1_000);
    },
    getDiagnostics(): RuntimePostgresClientDiagnostics {
      return {
        label: params.label,
        databaseName,
        generation,
        recoveries,
        lastFailure,
      };
    },
  };

  return new Proxy(target as RuntimePostgresClient<TClient>, {
    get(proxyTarget, prop, receiver) {
      if (prop in proxyTarget) {
        return Reflect.get(proxyTarget, prop, receiver);
      }

      const value = Reflect.get(activeClient as object, prop, activeClient);
      return typeof value === "function" ? value.bind(activeClient) : value;
    },
  });
}
